import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Grid, Paper, Avatar, Button, Chip, Card,
  CardContent, Alert, CircularProgress,
  List, ListItem, ListItemAvatar, ListItemText,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField
} from '@mui/material';
import {
  ArrowForward, VideoCall, Star, PlayCircle
} from '@mui/icons-material';
import { useAuth } from '../../store/authStore';
import { getUpcomingLessons, getTeacherBookings } from '../../api/booking';
import { createDailyRoom } from '../../api/teacher';
import { getTeacherEarnings } from '../../api/earnings';
import { getCurrentLanguage } from '../../utils/navigation';
import { getDateInTimezone, getTimeInTimezone } from '../../utils/timezone';

const TeacherHome = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const lang = getCurrentLanguage();

  const [upcomingLessons, setUpcomingLessons] = useState([]);
  const [pendingBookings, setPendingBookings] = useState([]);
  const [earnings, setEarnings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [liveDialog, setLiveDialog] = useState(false);
  const [liveTitle, setLiveTitle] = useState('');
  const [liveSubject, setLiveSubject] = useState('');
  const [liveMode, setLiveMode] = useState('zoom'); // 'zoom' or 'jitsi'
  const [zoomLink, setZoomLink] = useState('');
  const [startingLive, setStartingLive] = useState(false);
  const [liveCreated, setLiveCreated] = useState(false);
  const [createdUrl, setCreatedUrl] = useState('');

  const handleStartLive = async () => {
    if (!liveTitle.trim()) return;
    if (liveMode === 'zoom' && !zoomLink.trim()) return;
    setStartingLive(true);
    try {
      let finalUrl;
      let roomName;

      if (liveMode === 'zoom') {
        // Use the teacher's Zoom link
        finalUrl = zoomLink.trim();
        roomName = `zoom-${Date.now()}`;
      } else {
        // Generate a Jitsi room
        const roomId = Math.random().toString(36).substring(2, 10);
        roomName = `Lingoply-${roomId}`;
        finalUrl = `https://meet.jit.si/${roomName}`;
      }

      // Save to Firebase so students can see it
      await createDailyRoom(user.id, {
        title: liveTitle,
        description: liveMode === 'zoom' ? 'Zoom Meeting' : 'Jitsi Meeting',
        subject: liveSubject || 'Language',
        startTime: new Date().toISOString(),
        teacherName: user.displayName || 'Teacher',
        customUrl: finalUrl,
        platform: liveMode
      });

      setCreatedUrl(finalUrl);
      setLiveCreated(true);
    } catch (err) {
      console.error('Error starting live:', err);
    } finally {
      setStartingLive(false);
    }
  };

  useEffect(() => {
    loadDashboardData();
  }, [user?.id]);

  const loadDashboardData = async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const [lessons, bookings, earningsData] = await Promise.all([
        getUpcomingLessons(user.id, 'teacher').catch(() => []),
        getTeacherBookings(user.id, 'pending').catch(() => []),
        getTeacherEarnings(user.id).catch(() => null)
      ]);
      setUpcomingLessons(lessons);
      setPendingBookings(bookings);
      setEarnings(earningsData);
    } catch (err) {
      console.error('Dashboard load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  const getCurrencySymbol = (currency) => {
    switch (currency) {
      case 'EUR': return '€';
      case 'MAD': return 'د.م.';
      case 'GBP': return '£';
      default: return '$';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Welcome Banner */}
      <Paper sx={{ p: 4, mb: 3, background: 'linear-gradient(135deg, #F5A623 0%, #e09500 100%)', color: 'white' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h4" fontWeight={700}>
              {getGreeting()}, {user?.firstName || user?.displayName}
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              Thanks for all you do for your learners
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<PlayCircle />}
            onClick={() => { setLiveDialog(true); setLiveCreated(false); setCreatedUrl(''); setLiveTitle(''); setLiveSubject(''); setZoomLink(''); setLiveMode('jitsi'); }}
            sx={{
              bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', fontWeight: 600,
              '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
              display: { xs: 'none', sm: 'flex' }
            }}
          >
            Start Live Class
          </Button>
        </Box>
      </Paper>

      {/* Pending Bookings Alert */}
      {pendingBookings.length > 0 && (
        <Alert
          severity="warning" sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={() => navigate(`/${lang}/teacher/calendar`)}>
              View
            </Button>
          }
        >
          You have {pendingBookings.length} pending booking request{pendingBookings.length > 1 ? 's' : ''}
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" fontWeight={700} color="primary.main">
                {upcomingLessons.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">Upcoming Lessons</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" fontWeight={700} color="success.main">
                {getCurrencySymbol(user?.currency)}{earnings?.availableBalance?.toFixed(2) || '0.00'}
              </Typography>
              <Typography variant="body2" color="text.secondary">Available Balance</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" fontWeight={700} color="warning.main">
                {user?.totalStudents || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">Active Students</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                <Star sx={{ color: '#FFB400' }} />
                <Typography variant="h4" fontWeight={700}>
                  {user?.averageRating?.toFixed(1) || '—'}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">Rating</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Next Lessons */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" fontWeight={700}>Next lessons</Typography>
          <Button size="small" endIcon={<ArrowForward />} onClick={() => navigate(`/${lang}/teacher/calendar`)}>
            See all
          </Button>
        </Box>

        {upcomingLessons.length > 0 ? (
          <List disablePadding>
            {upcomingLessons.slice(0, 5).map((lesson) => (
              <ListItem
                key={lesson.id}
                sx={{
                  bgcolor: '#f8f9fa', borderRadius: 2, mb: 1, px: 2,
                  border: '1px solid #e0e0e0'
                }}
                secondaryAction={
                  lesson.zoomLink && (
                    <Button
                      variant="contained" size="small" startIcon={<VideoCall />}
                      onClick={() => window.open(lesson.zoomLink, '_blank')}
                    >
                      Join
                    </Button>
                  )
                }
              >
                <ListItemAvatar>
                  <Avatar sx={{ bgcolor: 'primary.light' }}>
                    {lesson.studentName?.charAt(0)}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={lesson.studentName}
                  secondary={(() => {
                    const tz = user?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
                    const dateStr = lesson.startAtUTC
                      ? getDateInTimezone(lesson.startAtUTC, tz)
                      : lesson.date;
                    const [y, mo, d] = dateStr.split('-').map(Number);
                    const dateLabel = new Date(y, mo - 1, d).toLocaleDateString('en', { weekday: 'short', month: 'short', day: 'numeric' });
                    const start = lesson.startAtUTC ? getTimeInTimezone(lesson.startAtUTC, tz) : lesson.startTime;
                    const end   = lesson.endAtUTC   ? getTimeInTimezone(lesson.endAtUTC,   tz) : lesson.endTime;
                    return `${dateLabel} · ${start} - ${end} · ${lesson.language || ''}`;
                  })()}
                />
                <Chip
                  label={lesson.status}
                  size="small"
                  color={lesson.status === 'confirmed' ? 'success' : 'warning'}
                  sx={{ mr: 8 }}
                />
              </ListItem>
            ))}
          </List>
        ) : (
          <Typography variant="body2" color="text.secondary" textAlign="center" py={3}>
            No upcoming lessons. Students can find you and book lessons from the marketplace.
          </Typography>
        )}
      </Paper>

      {/* Profile Status */}
      {!user?.profileApproved && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Your profile is pending admin approval. Once approved, students will be able to find and book lessons with you.
        </Alert>
      )}

      {user?.profileCompleted === false && (
        <Alert
          severity="warning" sx={{ mb: 3 }}
          action={
            <Button color="inherit" onClick={() => navigate(`/${lang}/teacher/register`)}>
              Complete Profile
            </Button>
          }
        >
          Complete your profile to start receiving bookings from students.
        </Alert>
      )}
      {/* Start Live Class Dialog */}
      <Dialog open={liveDialog} onClose={() => { setLiveDialog(false); setLiveCreated(false); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {liveCreated ? 'Live Class Started!' : 'Start Live Class'}
        </DialogTitle>
        <DialogContent>
          {liveCreated ? (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <PlayCircle sx={{ fontSize: 60, color: '#4CAF50', mb: 2 }} />
              <Typography variant="body1" gutterBottom>
                Your class is live! Students can now see and join it.
              </Typography>
              <TextField
                fullWidth value={createdUrl} sx={{ mt: 1, mb: 2 }}
                InputProps={{ readOnly: true }}
                onClick={(e) => { e.target.select(); navigator.clipboard?.writeText(createdUrl); }}
                helperText="Click to copy link"
              />
              <Button
                variant="contained" fullWidth
                onClick={() => window.open(createdUrl, '_blank')}
                startIcon={<VideoCall />}
                sx={{ bgcolor: '#4CAF50', '&:hover': { bgcolor: '#388E3C' }, py: 1.5 }}
              >
                Join Your Class
              </Button>
            </Box>
          ) : (
            <Box sx={{ pt: 1 }}>
              <TextField
                fullWidth label="Class Title *" value={liveTitle}
                onChange={(e) => setLiveTitle(e.target.value)}
                sx={{ mb: 2 }}
                placeholder="e.g. Arabic Conversation Practice"
              />
              <TextField
                fullWidth label="Subject" value={liveSubject}
                onChange={(e) => setLiveSubject(e.target.value)}
                sx={{ mb: 3 }}
                placeholder="e.g. Arabic, French"
              />

              {/* Platform choice */}
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>Choose Platform</Typography>
              <Grid container spacing={1} sx={{ mb: 2 }}>
                <Grid item xs={6}>
                  <Paper
                    onClick={() => { setLiveMode('jitsi'); setZoomLink(''); }}
                    sx={{
                      p: 2, textAlign: 'center', cursor: 'pointer',
                      border: liveMode === 'jitsi' ? '2px solid #F5A623' : '1px solid #eee',
                      bgcolor: liveMode === 'jitsi' ? '#FFF8F0' : '#fff',
                      '&:hover': { borderColor: '#F5A623' }
                    }}
                  >
                    <Typography variant="body2" fontWeight={700} color={liveMode === 'jitsi' ? '#F5A623' : '#333'}>
                      Jitsi Meet
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Free · Screen share · No account needed
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper
                    onClick={() => setLiveMode('zoom')}
                    sx={{
                      p: 2, textAlign: 'center', cursor: 'pointer',
                      border: liveMode === 'zoom' ? '2px solid #2D8CFF' : '1px solid #eee',
                      bgcolor: liveMode === 'zoom' ? '#F0F7FF' : '#fff',
                      '&:hover': { borderColor: '#2D8CFF' }
                    }}
                  >
                    <Typography variant="body2" fontWeight={700} color={liveMode === 'zoom' ? '#2D8CFF' : '#333'}>
                      Zoom / Meet
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Paste your own meeting link
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              {liveMode === 'zoom' && (
                <TextField
                  fullWidth label="Meeting Link *" value={zoomLink}
                  onChange={(e) => setZoomLink(e.target.value)}
                  placeholder="https://zoom.us/j/... or https://meet.google.com/..."
                  helperText="Paste your Zoom, Google Meet, or any video call link"
                />
              )}

              {liveMode === 'jitsi' && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  A free Jitsi video room will be created automatically with screen sharing, chat, and recording.
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        {!liveCreated && (
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setLiveDialog(false)}>Cancel</Button>
            <Button
              variant="contained" onClick={handleStartLive}
              disabled={startingLive || !liveTitle.trim() || (liveMode === 'zoom' && !zoomLink.trim())}
              startIcon={startingLive ? <CircularProgress size={18} /> : <PlayCircle />}
              sx={{ bgcolor: '#F5A623', '&:hover': { bgcolor: '#e09500' } }}
            >
              {startingLive ? 'Starting...' : 'Go Live'}
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </Box>
  );
};

export default TeacherHome;
